import React from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getUrlToBackendApi, request } from '@services/api';

import { GET_ME_QUERY_KEY } from '@hooks/api/user/use-query-me';

import { ITEM_TYPE, LotteryPurchaseRequest, ShopErrorType, ShopMutationError, ShopPurchaseResponse } from '@types';

import { config } from '@/config.ts';

const brand = config.BRAND;

const isNgu3 = brand === 'ngu3';
const isRockets = brand === 'rockets';
const isBlitz = brand === 'blitz';
const isSk2 = brand === 'sk2';
const isCom2 = brand === 'com2';

const isExternalBrand = isNgu3 || isRockets || isSk2 || isCom2;

const getSuccessNotificationKey = (itemType: ITEM_TYPE) => {
	switch (true) {
		case itemType === ITEM_TYPE.MERCH_PRIZE || itemType === ITEM_TYPE.SHP_GIVEAWAY:
			return 'present_send_merch';
		default:
			return 'present_applied';
	}
};

export const useMutationBuyMerchPrize = () => {
	return useMutation<ShopPurchaseResponse, ShopMutationError, string>({
		mutationKey: ['shop', 'buy', 'merch_prize'],
		mutationFn: async (shopItemId: string) => {
			return await request<ShopPurchaseResponse>({
				method: 'post',
				url: getUrlToBackendApi(`/shop_items/${shopItemId}/buy_merch_prize`).href
			});
		}
	});
};

export const useMutationBuyReaBonus = () => {
	return useMutation<ShopPurchaseResponse, ShopMutationError, string>({
		mutationKey: ['shop', 'buy', 'rea_bonus'],
		mutationFn: async (shopItemId: string) => {
			return await request<ShopPurchaseResponse>({
				method: 'post',
				url: getUrlToBackendApi(`/shop_items/${shopItemId}/buy_rea_bonus`).href
			});
		}
	});
};

export const useMutationBuyLottery = () => {
	const queryClient = useQueryClient();

	return useMutation<
		ShopPurchaseResponse,
		ShopMutationError,
		{
			shopItemId: string;
			count: number;
		}
	>({
		mutationKey: ['shop', 'buy', 'lottery'],
		mutationFn: async ({ shopItemId, count }) => {
			const requestBody: LotteryPurchaseRequest = {
				shop_item: { count }
			};

			return await request<ShopPurchaseResponse>({
				method: 'post',
				url: getUrlToBackendApi(`/shop_items/${shopItemId}/buy_lottery`).href,
				data: requestBody
			});
		},
		onSuccess: () => {
			queryClient.refetchQueries();
		}
	});
};

export const useMutationBuyShpGiveaway = () => {
	return useMutation<ShopPurchaseResponse, ShopMutationError, string>({
		mutationKey: ['shop', 'buy', 'shp_giveaway'],
		mutationFn: async (shopItemId: string) => {
			return await request<ShopPurchaseResponse>({
				method: 'post',
				url: getUrlToBackendApi(`/shop_items/${shopItemId}/buy_shp_giveaway`).href
			});
		}
	});
};

interface PurchaseHandlers {
	onSuccess?: (response: ShopPurchaseResponse) => void;
	onError?: (error: ShopMutationError) => void;
	showDefaultNotifications?: boolean;
	updateBalance?: boolean;
}

export const useMutationBuyShopItem = (options: PurchaseHandlers = {}) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation(['main', 'errors']);

	const {
		onSuccess: customOnSuccess,
		onError: customOnError,
		showDefaultNotifications = true,
		updateBalance = true
	} = options;

	const shopErrors = React.useMemo(
		() => ({
			user_has_shop_disabled: t('errors:user_has_shop_disabled'),
			user_has_no_steam_trade_url: t('errors:user_has_no_steam_trade_url'),
			shop_item_not_published: t('errors:shop_item_not_published'),
			user_has_not_enough_pm_points: t(
				isBlitz
					? 'errors:user_has_not_enough_blitz_points'
					: isExternalBrand
						? 'errors:user_has_not_enough_points'
						: 'errors:user_has_not_enough_pm_points'
			),
			user_reached_limit_24h: t('errors:user_reached_limit_24h'),
			user_reached_limit_per_user: t('errors:user_reached_limit_per_user'),
			items_in_stock_reached_limit: t('errors:items_in_stock_reached_limit'),
			rea_bonus_callback_error: t('errors:rea_bonus_callback_error'),
			lottery_is_expired: t('errors:lottery_is_expired'),
			shop_item_is_not_lottery: t('errors:shop_item_is_not_lottery'),
			bad_item_params_error: t('errors:bad_item_params_error'),
			default: t('errors:default')
		}),
		[t]
	);

	const getDefaultErrorMessage = (errors: ShopErrorType[]): string => {
		for (const error of errors) {
			if (shopErrors[error]) {
				return shopErrors[error];
			}
		}
		return shopErrors.default;
	};

	const handleDefaultSuccess = (_response: ShopPurchaseResponse, itemType: ITEM_TYPE) => {
		if (showDefaultNotifications) {
			toast.success(t(`notifications:${getSuccessNotificationKey(itemType)}`), {
				hideProgressBar: true
			});
		}

		if (updateBalance) {
			queryClient.invalidateQueries({ queryKey: GET_ME_QUERY_KEY });
		}
	};

	const handleDefaultError = (error: ShopMutationError) => {
		if (showDefaultNotifications) {
			// All errors are HTTP Response format: errors are in error.data.errors
			const errors = error.data.errors || [];

			const errorMessage = getDefaultErrorMessage(errors);
			toast.error(errorMessage, {
				hideProgressBar: true
			});
		}
	};

	const handleSuccess = (response: ShopPurchaseResponse, itemType: ITEM_TYPE) => {
		handleDefaultSuccess(response, itemType);
		customOnSuccess?.(response);
	};

	const handleError = (error: ShopMutationError) => {
		handleDefaultError(error);
		customOnError?.(error);
	};

	const buyMerchPrize = useMutationBuyMerchPrize();
	const buyReaBonus = useMutationBuyReaBonus();
	const buyLottery = useMutationBuyLottery();
	const buyShpGiveaway = useMutationBuyShpGiveaway();

	const buyItem = (itemType: ITEM_TYPE, shopItemId: string, count?: number) => {
		const mutationOptions = {
			onSuccess: (response: ShopPurchaseResponse) => handleSuccess(response, itemType),
			onError: handleError
		};

		switch (itemType) {
			case ITEM_TYPE.MERCH_PRIZE:
				return buyMerchPrize.mutate(shopItemId, mutationOptions);
			case ITEM_TYPE.REA_BONUS:
				return buyReaBonus.mutate(shopItemId, mutationOptions);
			case ITEM_TYPE.SHP_GIVEAWAY:
				return buyShpGiveaway.mutate(shopItemId, mutationOptions);
			default:
				if (count) {
					return buyLottery.mutate({ shopItemId, count }, mutationOptions);
				}
				throw new Error('Count is required for lottery items');
		}
	};

	return {
		buyItem,
		isLoading: buyMerchPrize.isPending || buyReaBonus.isPending || buyLottery.isPending || buyShpGiveaway.isPending,
		error: buyMerchPrize.error || buyReaBonus.error || buyLottery.error || buyShpGiveaway.error,

		getDefaultErrorMessage
	};
};
